import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import TicketListModal, {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
} from "../../../core/modals/ticketListModal";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders } from "../../../common/Utils";
import "./tickets.css"; // Import the CSS file for ticket styles

// Define Ticket interface based on expected API response
interface Ticket {
  id: string;
  accountId: number;
  ticketNumber: string;
  title: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  createdBy: string;
  username: string;
  createdOn: string;
  updatedOn: string;
  userProfileImageUrl: string;
  commentCount?: number;
  attachmentsCount?: number;
}

const Tickets = () => {
  const routes = all_routes;
  const { callFetch, data, isLoading, error } = useFetch();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("last7days");
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 5;
  const [refreshing, setRefreshing] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<
    "success" | "danger" | "warning" | "info"
  >("success");

  // Helper functions to convert between backend values and display labels
  const getStatusLabel = (value: string): string => {
    const option = STATUS_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const getPriorityLabel = (value: string): string => {
    const option = PRIORITY_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // Function to fetch support tickets
  const fetchSupportTickets = async () => {
    setRefreshing(true);
    try {
      const response = await callFetch(
        API.GET_SUPPORT_TICKETS,
        "GET",
        getAPIHeaders(),
        null,
        null
      );
      if (response) {
        setTickets(response);
        setRefreshing(false);
      }
    } catch (error) {
      console.error("Error fetching support tickets:", error);
      setRefreshing(false);
    }
  };

  // Function to update ticket priority or status
  const updateTicket = async (
    ticketId: string,
    field: "priority" | "status",
    value: string
  ) => {
    try {
      const payload = {
        id: ticketId,
        [field]: value,
      };

      await callFetch(
        API.UPDATE_SUPPORT_TICKET,
        "PUT",
        getAPIHeaders(),
        null,
        payload
      );

      // Update the local state to reflect the change
      const updatedTickets = tickets.map((ticket) => {
        if (ticket.id === ticketId) {
          return {
            ...ticket,
            [field]: value,
            updatedOn: new Date().toISOString(),
          };
        }
        return ticket;
      });

      setTickets(updatedTickets);

      // Show success toast notification
      setToastMessage(`Ticket ${field} updated successfully`);
      setToastType("success");
      setShowToast(true);

      // Auto-hide toast after 2 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
    } catch (error) {
      console.error(`Error updating ticket ${field}:`, error);

      // Show error toast notification
      setToastMessage(`Failed to update ticket ${field}`);
      setToastType("danger");
      setShowToast(true);

      // Auto-hide toast after 2 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
    }
  };

  // Function to handle ticket creation
  const handleTicketCreated = () => {
    // Refresh the tickets list
    fetchSupportTickets();
  };

  // Call the API when component mounts
  useEffect(() => {
    fetchSupportTickets();
  }, []);

  // Format date to display how long ago the ticket was updated
  const formatTimeAgo = (dateString: string, prefix: string = 'Updated') => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 3600;
    if (interval < 24) {
      return `${prefix} ${Math.floor(interval)} hours ago`;
    }
    interval = seconds / 86400;
    return `${prefix} ${Math.floor(interval)} days ago`;
  };

  // Dynamically generate filter options from ticket data
  const statusOptions = useMemo(() => {
    const uniqueStatuses = Array.from(
      new Set(tickets.map((ticket) => ticket.status))
    );
    return uniqueStatuses.map((status) => {
      // Find the display label from STATUS_OPTIONS or use the value itself
      const option = STATUS_OPTIONS.find((opt) => opt.value === status);
      return {
        value: status,
        label: option ? option.label : status,
      };
    });
  }, [tickets]);

  const priorityOptions = useMemo(() => {
    const uniquePriorities = Array.from(
      new Set(tickets.map((ticket) => ticket.priority))
    );
    return uniquePriorities.map((priority) => {
      // Find the display label from PRIORITY_OPTIONS or use the value itself
      const option = PRIORITY_OPTIONS.find((opt) => opt.value === priority);
      return {
        value: priority,
        label: option ? option.label : priority,
      };
    });
  }, [tickets]);

  // Filter tickets based on search term, priority, status, and date
  const filteredTickets = tickets.filter((ticket) => {
    let matchesPriority = true;
    let matchesStatus = true;
    let matchesDate = true;
    let matchesSearch = true;

    // Search filter
    if (searchTerm) {
      matchesSearch =
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(ticket.id).toLowerCase().includes(searchTerm.toLowerCase());
    }

    // Priority filter
    if (priorityFilter && ticket.priority !== priorityFilter) {
      matchesPriority = false;
    }

    // Status filter
    if (statusFilter && ticket.status !== statusFilter) {
      matchesStatus = false;
    }

    // Date filter
    if (dateFilter) {
      const ticketDate = new Date(ticket.updatedOn);
      const now = new Date();

      // Reset time components to get accurate day comparisons
      const ticketDateNormalized = new Date(
        ticketDate.getFullYear(),
        ticketDate.getMonth(),
        ticketDate.getDate()
      );

      const todayNormalized = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );

      if (dateFilter === "last7days") {
        const sevenDaysAgo = new Date(todayNormalized);
        sevenDaysAgo.setDate(todayNormalized.getDate() - 7);
        if (ticketDateNormalized < sevenDaysAgo) {
          matchesDate = false;
        }
      } else if (dateFilter === "lastMonth") {
        const monthAgo = new Date(todayNormalized);
        monthAgo.setMonth(todayNormalized.getMonth() - 1);
        if (ticketDateNormalized < monthAgo) {
          matchesDate = false;
        }
      }
    }

    return matchesPriority && matchesStatus && matchesDate && matchesSearch;
  }).sort((a, b) => {
    return new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime();
  });

  // Calculate pagination
  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(
    indexOfFirstTicket,
    indexOfLastTicket
  );
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Tickets</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Tickets
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2 mb-2">
                <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                  <Link
                    to={routes.tickets}
                    className="btn btn-icon btn-sm active bg-primary text-white me-1"
                  >
                    <i className="ti ti-list-tree" />
                  </Link>
                  <Link to={routes.ticketGrid} className="btn btn-icon btn-sm">
                    <i className="ti ti-layout-grid" />
                  </Link>
                </div>
              </div>
              <div className="mb-2">
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_ticket"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Ticket
                </Link>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="card">
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Ticket List</h5>
                <div className="d-flex align-items-center flex-wrap row-gap-3">
                  <div className="me-2 mb-2 mb-md-0">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search tickets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <i className="ti ti-search position-absolute top-50 end-0 translate-middle-y me-2"></i>
                    </div>
                  </div>
                  <div className="dropdown me-2">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      {priorityFilter
                        ? getPriorityLabel(priorityFilter)
                        : "Priority"}
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                      {priorityOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            onClick={() => setPriorityFilter(option.value)}
                            className={`dropdown-item rounded-1 ${
                              priorityFilter === option.value ? "active" : ""
                            }`}
                          >
                            {option.label}
                          </button>
                        </li>
                      ))}
                      <li>
                        <button
                          onClick={() => setPriorityFilter("")}
                          className="dropdown-item rounded-1"
                        >
                          All Priorities
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div className="dropdown me-2">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      {statusFilter
                        ? getStatusLabel(statusFilter)
                        : "Select Status"}
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                      {statusOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            onClick={() => setStatusFilter(option.value)}
                            className={`dropdown-item rounded-1 ${
                              statusFilter === option.value ? "active" : ""
                            }`}
                          >
                            {option.label}
                          </button>
                        </li>
                      ))}
                      <li>
                        <button
                          onClick={() => setStatusFilter("")}
                          className={`dropdown-item rounded-1`}
                        >
                          All Statuses
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div className="dropdown me-2">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      Filter By:{" "}
                      {dateFilter === "last7days"
                        ? "Last 7 Days"
                        : dateFilter === "lastMonth"
                        ? "Last 30 Days"
                        : "All Time"}
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                      <li>
                        <button
                          onClick={() => {
                            setDateFilter("last7days");
                            setCurrentPage(1);
                          }}
                          className={`dropdown-item rounded-1 ${
                            dateFilter === "last7days" ? "active" : ""
                          }`}
                        >
                          Last 7 Days
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            setDateFilter("lastMonth");
                            setCurrentPage(1);
                          }}
                          className={`dropdown-item rounded-1 ${
                            dateFilter === "lastMonth" ? "active" : ""
                          }`}
                        >
                          Last 30 Days
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            setDateFilter("");
                            setCurrentPage(1);
                          }}
                          className={`dropdown-item rounded-1 ${
                            dateFilter === "" ? "active" : ""
                          }`}
                        >
                          All Time
                        </button>
                      </li>
                    </ul>
                  </div>
                  {(searchTerm ||
                    priorityFilter ||
                    statusFilter ||
                    dateFilter) && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => {
                        setSearchTerm("");
                        setPriorityFilter("");
                        setStatusFilter("");
                        setDateFilter("last7days");
                        setCurrentPage(1);
                      }}
                    >
                      <i className="ti ti-x me-1"></i>
                      Clear Filters
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-primary ms-2"
                    onClick={fetchSupportTickets}
                    disabled={refreshing}
                  >
                    <i
                      className={`ti ${
                        refreshing ? "ti-reload" : "ti-refresh"
                      } me-1`}
                    ></i>
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {isLoading ? (
                <div className="skeleton-loader">
                  <div className="card mb-3">
                    <div className="card-header d-flex justify-content-between">
                      <div className="skeleton-line w-25 mb-0"></div>
                      <div className="skeleton-badge"></div>
                    </div>
                    <div className="card-body">
                      <div className="skeleton-line w-25 mb-2"></div>
                      <div className="skeleton-line w-75 mb-3"></div>
                      <div className="d-flex justify-content-between">
                        <div className="skeleton-avatar-line"></div>
                        <div className="skeleton-avatar-line"></div>
                      </div>
                    </div>
                  </div>
                  <div className="card mb-3">
                    <div className="card-header d-flex justify-content-between">
                      <div className="skeleton-line w-25 mb-0"></div>
                      <div className="skeleton-badge"></div>
                    </div>
                    <div className="card-body">
                      <div className="skeleton-line w-25 mb-2"></div>
                      <div className="skeleton-line w-75 mb-3"></div>
                      <div className="d-flex justify-content-between">
                        <div className="skeleton-avatar-line"></div>
                        <div className="skeleton-avatar-line"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentTickets.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center p-4">
                    <div className="text-center p-4">
                      <i
                        className="ti ti-box-off text-muted"
                        style={{ fontSize: "64px" }}
                      ></i>
                      <h5>No tickets found</h5>
                    </div>
                    <p className="text-muted">
                      Create a new ticket to get started
                    </p>
                    <button
                      className="btn btn-primary mt-2"
                      data-bs-toggle="modal"
                      data-bs-target="#add_ticket"
                    >
                      <i className="ti ti-circle-plus me-1"></i>
                      Add New Ticket
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {currentTickets.map((ticket) => (
                    <div className="card mb-3 ticket-card" key={ticket.id}>
                      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h5 className="text-info fw-medium">{ticket.title}</h5>
                        <div className="d-flex align-items-center">
                          <div className="dropdown">
                            <span
                              className={`badge badge-${
                                ticket.priority === "HIGH"
                                  ? "danger"
                                  : ticket.priority === "MEDIUM"
                                  ? "warning"
                                  : "success"
                              } d-inline-flex align-items-center position-relative`}
                              role="button"
                              data-bs-toggle="dropdown"
                            >
                              <i className="ti ti-circle-filled fs-5 me-1" />
                              {getPriorityLabel(ticket.priority)}
                              <span
                                className="edit-icon-wrapper ms-1 bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{ width: "18px", height: "18px" }}
                              >
                                <i className="ti ti-edit fs-6 text-primary" />
                              </span>
                            </span>
                            <ul className="dropdown-menu p-2">
                              <li className="dropdown-header">
                                Change Priority
                              </li>
                              {PRIORITY_OPTIONS.filter(
                                (opt) => opt.value !== "Select"
                              ).map((option) => (
                                <li key={option.value}>
                                  <button
                                    onClick={() =>
                                      updateTicket(
                                        ticket.id,
                                        "priority",
                                        option.value
                                      )
                                    }
                                    className={`dropdown-item rounded-1 ${
                                      ticket.priority === option.value
                                        ? "active"
                                        : ""
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div>
                          <span className="badge badge-info rounded-pill mb-2">
                            Ticket# {ticket.id}
                          </span>
                          <div className="d-flex align-items-center mb-2">
                            <h5 className="fw-semibold me-2">
                              <Link to={`${routes.ticketDetails}/${ticket.id}`}>
                                {ticket.subject}
                              </Link>
                            </h5>
                            <div className="dropdown">
                              <span
                                className={`badge ${
                                  ticket.status === "OPEN"
                                    ? "bg-success"
                                    : ticket.status === "CLOSED"
                                    ? "bg-secondary"
                                    : ticket.status === "ON_HOLD"
                                    ? "bg-warning"
                                    : "bg-info"
                                } d-flex align-items-center ms-1 position-relative`}
                                role="button"
                                data-bs-toggle="dropdown"
                              >
                                <i className="ti ti-circle-filled fs-5 me-1" />
                                {getStatusLabel(ticket.status)}
                                <span
                                  className="edit-icon-wrapper ms-1 bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                  style={{ width: "18px", height: "18px" }}
                                >
                                  <i className="ti ti-edit fs-6 text-primary" />
                                </span>
                              </span>
                              <ul className="dropdown-menu p-2">
                                <li className="dropdown-header">
                                  Change Status
                                </li>
                                {STATUS_OPTIONS.filter(
                                  (opt) => opt.value !== "Select"
                                ).map((option) => (
                                  <li key={option.value}>
                                    <button
                                      onClick={() =>
                                        updateTicket(
                                          ticket.id,
                                          "status",
                                          option.value
                                        )
                                      }
                                      className={`dropdown-item rounded-1 ${
                                        ticket.status === option.value
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      {option.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="d-flex align-items-center flex-wrap row-gap-2">
                            {ticket.createdBy && (
                              <p className="d-flex align-items-center mb-0 me-2">
                                <img
                                  src={
                                    ticket.userProfileImageUrl ||
                                    "/assets/img/profiles/user-default.png"
                                  }
                                  className="avatar avatar-xs rounded-circle me-2"
                                  alt="img"
                                />
                                Created by{" "}
                                <span className="text-dark ms-1">
                                  {ticket.username}
                                </span>
                              </p>
                            )}
                            <p className="d-flex align-items-center mb-0 me-2">
                              <i className="ti ti-calendar-bolt me-1" />
                              {formatTimeAgo(ticket.createdOn, 'Created')}
                            </p>
                            <p className="d-flex align-items-center mb-0 me-2">
                              <i className="ti ti-calendar-bolt me-1" />
                              {formatTimeAgo(ticket.updatedOn)}
                            </p>
                            <div className="d-flex align-items-center mb-0 ms-auto">
                              <ImageWithBasePath
                                src={"assets/img/profiles/user-default.png"}
                                className="avatar avatar-xs rounded-circle me-2"
                                alt="img"
                              />{" "}
                              Assigned to{" "}
                              <span className="text-dark ms-1">
                                Sanchalak Support
                              </span>
                            </div>
                          </div>
                          <div className="ticket-actions mt-3 d-flex align-items-center">
                            <Link
                              to={`${routes.ticketDetails}/${ticket.id}`}
                              className="btn btn-primary btn-sm"
                            >
                              <i className="ti ti-eye me-1"></i>
                              View Details
                            </Link>
                            {/* <div className="ms-2 d-flex align-items-center">
                              <i className="ti ti-message-circle-2 text-muted me-1"></i>
                              <span className="ticket-comments-count">
                                {ticket.commentCount || 0} Comments
                              </span>
                            </div> */}
                            {ticket.attachmentsCount &&
                              ticket.attachmentsCount > 0 && (
                                <div className="ms-3 d-flex align-items-center">
                                  <i className="ti ti-paperclip text-muted me-1"></i>
                                  <span className="ticket-attachment-count">
                                    {ticket.attachmentsCount} Attachments
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pagination UI */}
                  {filteredTickets.length > ticketsPerPage && (
                    <nav aria-label="Page navigation" className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="ti ti-chevron-left"></i>
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li
                            key={index}
                            className={`page-item ${
                              currentPage === index + 1 ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="ti ti-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2014 - 2025 © SmartHR.</p>
        </div>
      </div>
      {/* /Page Wrapper */}

      <TicketListModal onTicketCreated={handleTicketCreated} />

      {/* Toast notification */}
      {showToast && (
        <div
          className="position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1050 }}
        >
          <div
            className={`toast show align-items-center text-white bg-${toastType} border-0`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">
                <i
                  className={`me-2 ti ${
                    toastType === "success"
                      ? "ti-circle-check"
                      : toastType === "danger"
                      ? "ti-alert-circle"
                      : toastType === "warning"
                      ? "ti-alert-triangle"
                      : "ti-info-circle"
                  }`}
                ></i>
                {toastMessage}
              </div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setShowToast(false)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Tickets;
